import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type {
  ConnectorDraft,
  ConnectorExecutionDto,
  UploadType,
} from '../models/dataConnector.types'
import { executionRows } from '../service/connectorPresenter'

import type { ApiError } from '@/shared/http'
import type { UiAsyncState } from '@/shared/ui'

function blankDraft(): ConnectorDraft {
  return {
    step: 1,
    editingId: null,
    uploadTypeId: '',
    source: null,
    frequency: null,
    projectId: '',
    datasetId: '',
    tableId: '',
    sheetUrl: '',
    sheetName: '',
    accountMode: 'input',
    accountId: '',
    audienceMode: 'current',
    audienceId: '',
    audienceName: '',
    audienceDescription: '',
    advertiserId: '',
    extra: {},
    mappings: {},
  }
}

export const useConnectorStore = defineStore('dataConnectors', () => {
  const status = ref<UiAsyncState>('loading')
  const error = ref<ApiError | null>(null)
  const uploadTypes = ref<UploadType[]>([])
  const executions = ref<ConnectorExecutionDto[]>([])
  const mode = ref<'list' | 'wizard'>('list')
  const draft = ref<ConnectorDraft>(blankDraft())
  const saving = ref(false)
  const deletingId = ref<number | string | null>(null)

  const rows = computed(() => executionRows(executions.value, uploadTypes.value))
  const currentUploadType = computed(
    () => uploadTypes.value.find((item) => item.id === draft.value.uploadTypeId) ?? null,
  )
  const isEmpty = computed(() => status.value === 'empty')

  function setLoading(): void {
    status.value = 'loading'
    error.value = null
  }

  function setLoaded(types: UploadType[], list: ConnectorExecutionDto[]): void {
    uploadTypes.value = types
    executions.value = list
    status.value = list.length > 0 ? 'ready' : 'empty'
    error.value = null
  }

  function setError(next: ApiError): void {
    error.value = next
    status.value = 'error'
  }

  function startCreate(): void {
    draft.value = blankDraft()
    mode.value = 'wizard'
  }

  function startEdit(execution: ConnectorExecutionDto): boolean {
    const type = uploadTypes.value.find((candidate) => candidate.id === execution.upload_type)
    if (!type) return false
    const info = execution.additional_info
    const extra = Object.fromEntries(
      (type.moreInputs ?? []).map((field) => [field.field, field.value ?? info[field.field] ?? '']),
    ) as Record<string, string | number>
    draft.value = {
      ...blankDraft(),
      step: 2,
      editingId: execution.id,
      uploadTypeId: execution.upload_type,
      source: info.source,
      frequency: info.frequency,
      projectId: String(info.project_id ?? ''),
      datasetId: String(info.dataset_id ?? ''),
      tableId: String(info.table_id ?? ''),
      sheetUrl: String(info.sheet_id ?? ''),
      sheetName: String(info.sheet_name ?? ''),
      accountId: type.accountPushField ? String(info[type.accountPushField] ?? '') : '',
      audienceMode: info.audience_id ? 'current' : 'new',
      audienceId: String(info.audience_id ?? ''),
      audienceName: String(info.name ?? info.custom_audience_name ?? ''),
      audienceDescription: String(info.description ?? info.custom_audience_description ?? ''),
      advertiserId: String(info.advertiser_id ?? ''),
      extra,
      mappings: { ...(info.field_mappings ?? {}) },
    }
    mode.value = 'wizard'
    return true
  }

  function chooseUploadType(id: string): void {
    if (draft.value.uploadTypeId === id) return
    draft.value.uploadTypeId = id
    draft.value.accountId = ''
    draft.value.accountMode = 'input'
    draft.value.audienceMode = 'current'
    draft.value.audienceId = ''
    draft.value.audienceName = ''
    draft.value.audienceDescription = ''
    draft.value.advertiserId = ''
    draft.value.extra = {}
    draft.value.mappings = {}
  }

  function discardDraft(): void {
    draft.value = blankDraft()
    mode.value = 'list'
  }

  function resetState(): void {
    status.value = 'loading'
    error.value = null
    uploadTypes.value = []
    executions.value = []
    mode.value = 'list'
    draft.value = blankDraft()
    saving.value = false
    deletingId.value = null
  }

  return {
    status,
    error,
    uploadTypes,
    executions,
    mode,
    draft,
    saving,
    deletingId,
    rows,
    currentUploadType,
    isEmpty,
    setLoading,
    setLoaded,
    setError,
    startCreate,
    startEdit,
    chooseUploadType,
    discardDraft,
    resetState,
  }
})

export type ConnectorStore = ReturnType<typeof useConnectorStore>
